import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import TablePagination from "./TablePagination";
import { getPastExpos, getStallsByExpo } from "../Apis/ExpoAPIs";
import { formatDate, formatDateTime } from "../helper/DateHelper";
import usePermissions from "../hook/userPermission";
import { getCategories } from "../Apis/CategoryAPIs";
import { getProducts } from "../Apis/ProductAPIs";
import { getLocations } from "../Apis/MasterAPIs";
import { getStallTypes } from "../Apis/StallTypeAPIs";
import { getManagers } from "../Apis/ManagerAPIs";
import { getImageUrl } from "../Apis/api";
import {
  getClients,
  getClientFollowups,
  getClientFollowupPayments,
  submitAdminPayment,
  submitDirectStallBooking,
  editStallFinancials,
  verifyPaymentStatus,
  updateClientFollowupStatus,
} from "../Apis/ClientAPIs";

const formatIndianNumber = (val) => {
  if (!val && val !== 0) return "";
  let str = val.toString().replace(/,/g, "");
  if (isNaN(str)) return str;

  let parts = str.split(".");
  let lastThree = parts[0].substring(parts[0].length - 3);
  let otherNumbers = parts[0].substring(0, parts[0].length - 3);
  if (otherNumbers !== "") {
    lastThree = "," + lastThree;
  }
  let res = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
  if (parts.length > 1) {
    res += "." + parts[1];
  }
  return res;
};

const PastExpoLayer = () => {
  const { hasPermission } = usePermissions();

  // Past Expo List State
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // View & Selected Expo State
  const [activeView, setActiveView] = useState("table"); // "table" | "client-followup-list"
  const [selectedExpo, setSelectedExpo] = useState(null);

  // Client Followup List State
  const [clientFollowupsList, setClientFollowupsList] = useState([]);
  const [clientFollowupsLoading, setClientFollowupsLoading] = useState(false);
  const [clientFollowupsPage, setClientFollowupsPage] = useState(0);
  const [clientFollowupsLimit, setClientFollowupsLimit] = useState(10);
  const [clientFollowupsTotal, setClientFollowupsTotal] = useState(0);
  const [clientSearchInput, setClientSearchInput] = useState("");
  const [clientFollowupSearchTerm, setClientFollowupSearchTerm] = useState("");
  const [selectedManagerFilter, setSelectedManagerFilter] = useState(null);
  const [clientFollowupStatus, setClientFollowupStatus] = useState("Blocked");
  const clientSearchTimeout = useRef(null);

  // Options State
  const [stallOptions, setStallOptions] = useState([]);
  const [stallTypeOptions, setStallTypeOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [managersList, setManagersList] = useState([]);

  // Direct Booking Modal State
  const [showDirectBookingModal, setShowDirectBookingModal] = useState(false);
  const [directBookingSubmitting, setDirectBookingSubmitting] = useState(false);
  const [bookingCreatorType, setBookingCreatorType] = useState("Myself");
  const [bookingCreatorManagerId, setBookingCreatorManagerId] = useState("");
  const [newEnquiryData, setNewEnquiryData] = useState({
    name: "",
    companyName: "",
    mobileNo: "",
    email: "",
    category: "",
    interestedProduct: [],
    segment: "B2B",
    designation: "",
    interestedLocations: [],
    address: "",
    city: "",
    state: "",
    pincode: "",
    status: "Booked",
    remark: "",
    followUpDate: "",
    stallNo: "",
    sqft: "",
    sqm: "",
    totalAmount: "",
    amountPaid: "",
    stallType: "",
    manualCost: "",
    paymentMethod: "",
    paymentStatus: "",
    paymentScreenshot: null,
    discount: 0,
  });

  // Block Detail Modal & Financials State
  const [showBlockDetailModal, setShowBlockDetailModal] = useState(false);
  const [selectedBlockItem, setSelectedBlockItem] = useState(null);
  const [selectedBlockPayments, setSelectedBlockPayments] = useState([]);
  const [editingStallNo, setEditingStallNo] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editRemarks, setEditRemarks] = useState("");
  const [editAddons, setEditAddons] = useState([]);
  const [editSaving, setEditSaving] = useState(false);

  // Admin Direct Payment Form State
  const [adminPaymentMethod, setAdminPaymentMethod] = useState("");
  const [adminPaymentAmount, setAdminPaymentAmount] = useState("");
  const [adminPaymentRemarks, setAdminPaymentRemarks] = useState("");
  const [adminPaymentProofFile, setAdminPaymentProofFile] = useState(null);
  const [adminPaymentTransactionId, setAdminPaymentTransactionId] = useState("");
  const [adminPaymentBankName, setAdminPaymentBankName] = useState("");
  const [adminPaymentBranchName, setAdminPaymentBranchName] = useState("");
  const [adminPaymentSubmitting, setAdminPaymentSubmitting] = useState(false);

  // Initial Past Expos Fetch
  const fetchPastExpos = async () => {
    setLoading(true);
    const res = await getPastExpos({
      page: currentPage,
      limit: rowsPerPage,
      search: searchTerm,
    });
    if (res.status) {
      setData(res.response?.data || res.response || []);
      setTotalRecords(
        res.response?.total ||
        (res.response?.data || res.response || []).length,
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPastExpos();
  }, [currentPage, rowsPerPage, searchTerm]);

  // Master options fetch
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [prodRes, locRes, stallTypeRes, mgrRes] = await Promise.all([
          getProducts(),
          getLocations(),
          getStallTypes(),
          getManagers(),
        ]);
        if (prodRes.status) {
          const list = prodRes.response?.data || prodRes.response || [];
          setProductOptions(
            list.map((p) => ({ value: p._id || p.id, label: p.productName || p.name }))
          );
        }
        if (locRes.status) {
          const list = locRes.response?.data || locRes.response || [];
          setLocationOptions(
            list.map((l) => ({ value: l._id || l.id, label: l.name || l.locationName }))
          );
        }
        if (stallTypeRes.status) {
          const list = stallTypeRes.response?.data || stallTypeRes.response || [];
          setStallTypeOptions(
            list.map((s) => ({ value: s._id || s.id, label: s.stallTypeName || s.name }))
          );
        }
        if (mgrRes.status) {
          const list = mgrRes.response?.data || mgrRes.response || [];
          setManagersList(
            list.map((m) => ({ value: m._id || m.id, label: m.name }))
          );
        }
      } catch (err) {
        console.error("Error loading master options:", err);
      }
    };
    fetchOptions();
  }, []);

  // Fetch Client Followups List for Selected Past Expo
  const fetchClientFollowupsList = async (
    expoId,
    status = "Blocked",
    page = 0,
    limit = 10,
    searchVal = "",
    managerVal = null
  ) => {
    setClientFollowupsLoading(true);
    try {
      const params = {
        page,
        limit,
        status,
        expoId,
      };
      if (searchVal) params.search = searchVal;
      if (managerVal) params.managerId = managerVal.value || managerVal;

      const res = await getClientFollowups(params);
      if (res.status) {
        const listData = Array.isArray(res.response?.data)
          ? res.response.data
          : res.response?.data?.docs || res.response?.docs || [];
        const total =
          typeof res.response?.total === "number"
            ? res.response.total
            : res.response?.data?.total || res.response?.total || 0;

        setClientFollowupsList(listData);
        setClientFollowupsTotal(total);
      }
    } catch (err) {
      console.error("Error fetching client followups:", err);
    } finally {
      setClientFollowupsLoading(false);
    }
  };

  useEffect(() => {
    const expoId = selectedExpo?._id || selectedExpo?.id;
    if (activeView === "client-followup-list" && expoId) {
      fetchClientFollowupsList(
        expoId,
        clientFollowupStatus,
        clientFollowupsPage,
        clientFollowupsLimit,
        clientFollowupSearchTerm,
        selectedManagerFilter
      );
    }
  }, [
    activeView,
    selectedExpo,
    clientFollowupStatus,
    clientFollowupsPage,
    clientFollowupsLimit,
    clientFollowupSearchTerm,
    selectedManagerFilter,
  ]);

  const handleClientFollowupSearch = (val) => {
    setClientSearchInput(val);
    if (clientSearchTimeout.current) clearTimeout(clientSearchTimeout.current);
    clientSearchTimeout.current = setTimeout(() => {
      setClientFollowupSearchTerm(val);
      setClientFollowupsPage(0);
    }, 500);
  };

  // Fetch stalls for selected expo (for Direct Stall Booking)
  const fetchExpoStalls = async (searchVal = "") => {
    const expoId = selectedExpo?._id || selectedExpo?.id;
    if (!expoId) return;
    try {
      const res = await getStallsByExpo(expoId, { search: searchVal });
      if (res.status) {
        const stalls = res.response?.data || res.response || [];
        setStallOptions(
          stalls.map((s) => ({
            value: s.stallNo,
            label: `Stall ${s.stallNo} (${s.sqm || 0} sqm) - ${s.status || "Available"}`,
            sqm: s.sqm,
            withgstvalue: s.withgstvalue || s.totalAmount || s.amount,
            stallValue: s.stallValue || s.amount,
            type: s.stallType?._id || s.stallType || "",
            status: s.status,
          }))
        );
      }
    } catch (err) {
      console.error("Error fetching expo stalls:", err);
    }
  };

  // Mobile Change handler for Direct Stall Booking
  const handleMobileChange = async (e) => {
    const val = e.target.value.replace(/\D/g, "");
    if (val.length <= 10) {
      setNewEnquiryData((prev) => ({ ...prev, mobileNo: val }));
    }
    if (val.length === 10) {
      try {
        const res = await getClients({ search: val, limit: 1 });
        if (res.status && res.response?.data && res.response.data.length > 0) {
          const client = res.response.data[0];
          setNewEnquiryData((prev) => {
            const fetchedProducts =
              Array.isArray(client.interestedProduct) && client.interestedProduct.length > 0
                ? client.interestedProduct.map((p) => p._id || p.id || p)
                : prev.interestedProduct;
            const fetchedLocations =
              Array.isArray(client.interestedLocations) && client.interestedLocations.length > 0
                ? client.interestedLocations.map((l) => l._id || l.id || l)
                : prev.interestedLocations;

            return {
              ...prev,
              name: client.keyPerson || "",
              companyName: client.companyName || "",
              email: client.email || "",
              designation: client.designation || "",
              city: client.city || "",
              interestedProduct: fetchedProducts,
              interestedLocations: fetchedLocations,
            };
          });
          toast.success("Client details fetched successfully!");
        }
      } catch (err) {
        console.error("Error fetching client by mobile:", err);
      }
    }
  };

  // Submit Direct Stall Booking
  const handleDirectBookingSubmit = async (e) => {
    e.preventDefault();
    if (!newEnquiryData.mobileNo || newEnquiryData.mobileNo.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!newEnquiryData.name) {
      toast.error("Please enter name");
      return;
    }
    if (!newEnquiryData.stallNo) {
      toast.error("Please select a stall");
      return;
    }

    setDirectBookingSubmitting(true);
    try {
      const payload = {
        Mobileno: newEnquiryData.mobileNo,
        name: newEnquiryData.name,
        company: newEnquiryData.companyName,
        email: newEnquiryData.email,
        productOfIntrest: newEnquiryData.interestedProduct,
        intrestLocation: newEnquiryData.interestedLocations,
        designation: newEnquiryData.designation,
        city: newEnquiryData.city,
        stallNo: newEnquiryData.stallNo,
        stallType: newEnquiryData.stallType,
        totalAmount: newEnquiryData.totalAmount,
        discount: newEnquiryData.discount,
        sqm: newEnquiryData.sqm,
        expoId: selectedExpo?._id || selectedExpo?.id,
        remarks: newEnquiryData.remark || `Stall booked directly by Admin`,
      };

      if ((bookingCreatorType === "Manager" || bookingCreatorType === "Overall") && bookingCreatorManagerId) {
        payload.createdBy = bookingCreatorManagerId;
      }

      const res = await submitDirectStallBooking(payload);
      if (res.status) {
        toast.success("Stall booked and approved successfully!");
        setShowDirectBookingModal(false);
        fetchClientFollowupsList(
          selectedExpo?._id || selectedExpo?.id,
          clientFollowupStatus,
          clientFollowupsPage,
          clientFollowupsLimit,
          clientFollowupSearchTerm,
          selectedManagerFilter
        );
      } else {
        toast.error(res.response?.message || "Failed to book stall");
      }
    } catch (err) {
      console.error("Error submitting direct booking:", err);
      toast.error("An error occurred during submission");
    } finally {
      setDirectBookingSubmitting(false);
    }
  };

  // Submit Admin Direct Payment
  const handleAdminPaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBlockItem) return;

    if (adminPaymentMethod === "UPI" && !adminPaymentProofFile) {
      toast.error("Payment proof image is required for UPI payments");
      return;
    }
    if (adminPaymentMethod === "Cash" && (!adminPaymentRemarks || adminPaymentRemarks.trim() === "")) {
      toast.error("Remarks are required for Cash payments");
      return;
    }
    if (adminPaymentMethod === "Cheque") {
      if (!adminPaymentTransactionId || adminPaymentTransactionId.trim() === "") {
        toast.error("Cheque No / Transaction ID is required");
        return;
      }
      if (!adminPaymentBankName || adminPaymentBankName.trim() === "") {
        toast.error("Bank Name is required");
        return;
      }
      if (!adminPaymentBranchName || adminPaymentBranchName.trim() === "") {
        toast.error("Branch Name is required");
        return;
      }
    }

    setAdminPaymentSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("clientFollowupId", selectedBlockItem._id);
      formData.append("amountPaid", adminPaymentAmount);
      formData.append("paymentMethod", adminPaymentMethod);
      if (adminPaymentRemarks) formData.append("remarks", adminPaymentRemarks);
      if (adminPaymentProofFile) formData.append("proof", adminPaymentProofFile);
      if (adminPaymentMethod === "Cheque") {
        formData.append("transactionId", adminPaymentTransactionId);
        formData.append("bankName", adminPaymentBankName);
        formData.append("branchName", adminPaymentBranchName);
      }

      const res = await submitAdminPayment(formData);
      if (res.status) {
        toast.success("Payment submitted successfully");
        setAdminPaymentMethod("");
        setAdminPaymentAmount("");
        setAdminPaymentProofFile(null);
        setAdminPaymentRemarks("");
        setAdminPaymentTransactionId("");
        setAdminPaymentBankName("");
        setAdminPaymentBranchName("");
        const fileInput = document.getElementById("adminPaymentProofInput");
        if (fileInput) fileInput.value = "";

        const pRes = await getClientFollowupPayments(selectedBlockItem._id);
        if (pRes.status) {
          setSelectedBlockPayments(pRes.response?.data || pRes.response || []);
        }
        fetchClientFollowupsList(
          selectedExpo?._id || selectedExpo?.id,
          clientFollowupStatus,
          clientFollowupsPage,
          clientFollowupsLimit,
          clientSearchInput,
          selectedManagerFilter
        );
      } else {
        toast.error(res.response?.message || "Failed to submit payment");
      }
    } catch (err) {
      console.error("Error submitting payment:", err);
      toast.error("An error occurred while submitting payment");
    } finally {
      setAdminPaymentSubmitting(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12 border-0 shadow-sm overflow-hidden">
      {activeView === "table" ? (
        <>
          <div
            className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
            style={{
              background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)",
            }}
          >
            <h6 className="text-white mb-0 fw-bold">Past Expo Reviews</h6>
            <div className="d-flex align-items-center gap-3">
              <Link
                to="/past-expo/expo-brands"
                className="btn text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600", height: "44px" }}
              >
                <Icon icon="ic:baseline-plus" className="text-xl" />
                Add Logo
              </Link>
              <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control radius-8 ps-40 py-10 bg-white border-0"
                    style={{ height: "44px" }}
                    placeholder="Search Name/Manager..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Icon
                    icon="solar:magnifer-linear"
                    className="position-absolute start-0 top-50 translate-middle-y ms-12 text-black opacity-75"
                  />
                </div>
              </form>
            </div>
          </div>
          <div className="card-body p-24">
            <div className="table-responsive border radius-8 overflow-x-auto mb-16 shadow-sm">
              <table className="table bordered-table sm-table mb-0" style={{ minWidth: "900px" }}>
                <thead>
                  <tr
                    style={{
                      background: "linear-gradient(135deg, #f8f9fa, #e9ecef)",
                    }}
                  >
                    <th scope="col" className="fw-bold text-dark py-16 px-20">
                      S.No
                    </th>
                    <th scope="col" className="fw-bold text-dark py-16 px-20">
                      Expo Name
                    </th>
                    <th
                      scope="col"
                      className="fw-bold text-dark py-16 px-20 text-left"
                      style={{ minWidth: "180px", maxWidth: "250px" }}
                    >
                      Manager
                    </th>
                    <th
                      scope="col"
                      className="fw-bold text-dark py-16 px-20 text-center"
                    >
                      Booked Stalls
                    </th>
                    <th
                      scope="col"
                      className="fw-bold text-dark py-16 px-20 text-center"
                    >
                      Visitors
                    </th>
                    <th
                      scope="col"
                      className="fw-bold text-dark py-16 px-20 text-center"
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-24">
                        Loading...
                      </td>
                    </tr>
                  ) : data.length > 0 ? (
                    data.map((item, index) => {
                      const managerText = Array.isArray(item.eventManager)
                        ? item.eventManager
                          .map((m) => m.name)
                          .filter(Boolean)
                          .join(", ")
                        : item.eventManager?.name || item.manager || "N/A";

                      return (
                        <tr
                          key={item._id || item.id}
                          className="transition-all hover-bg-light"
                        >
                          <td className="py-12 px-20 text-xs text-secondary-light">
                            {currentPage * rowsPerPage + index + 1}
                          </td>
                          <td className="py-12 px-20">
                            <div>{item.expoName || item.name}</div>
                            <div className="text-xxs text-secondary-light d-flex align-items-center gap-1 mt-4">
                              <Icon icon="solar:calendar-linear" />
                              {formatDate(item.startDate)} to{" "}
                              {formatDate(item.endDate)}
                            </div>
                          </td>
                          <td className="py-12 px-20 text-left" style={{ maxWidth: "260px" }}>
                            <div
                              title={managerText}
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "normal",
                                wordBreak: "break-word"
                              }}
                            >
                              {managerText}
                            </div>
                          </td>
                          <td className="py-12 px-20 text-center fw-bold">
                            <span
                              className="text-primary-600 cursor-pointer text-hover-underline"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setSelectedExpo(item);
                                setClientFollowupStatus("Blocked");
                                setClientFollowupsPage(0);
                                setClientSearchInput("");
                                setClientFollowupSearchTerm("");
                                setSelectedManagerFilter(null);
                                setActiveView("client-followup-list");
                              }}
                              title="View Stall Bookings & Payments"
                            >
                              {item.stats?.bookedStallsCount || item.booked || 0}
                            </span>
                          </td>
                          <td className="py-12 px-20 text-center fw-bold">
                            {(
                              item.stats?.visitorsCount ||
                              item.visitors ||
                              0
                            ).toLocaleString()}
                          </td>
                          <td className="py-12 px-20 text-center">
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              {hasPermission("past_expo", "view") && (
                                <Link
                                  to={`/past-expo/view/${item._id || item.id}`}
                                  state={{ from: "/present-expo" }}
                                  className="glass-action-btn glass-btn-view"
                                  title="View Expo"
                                >
                                  <Icon icon="lucide:eye" className="text-lg" />
                                </Link>
                              )}
                              <button
                                type="button"
                                className="glass-action-btn glass-btn-layout"
                                title="Stall Bookings & Payments"
                                onClick={() => {
                                  setSelectedExpo(item);
                                  setClientFollowupStatus("Blocked");
                                  setClientFollowupsPage(0);
                                  setClientSearchInput("");
                                  setClientFollowupSearchTerm("");
                                  setSelectedManagerFilter(null);
                                  setActiveView("client-followup-list");
                                }}
                              >
                                <Icon icon="solar:lock-keyhole-bold" className="text-lg" />
                              </button>
                              {hasPermission("past_expo", "edit") && (
                                <Link
                                  to={`/past-expo/gallery/${item._id || item.id}`}
                                  className="glass-action-btn glass-btn-edit"
                                  title="Manage Gallery"
                                >
                                  <Icon icon="solar:gallery-bold" className="text-lg" />
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-24">
                        No past expos found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <TablePagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalRecords / rowsPerPage) || 1}
              onPageChange={setCurrentPage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(0);
              }}
              totalRecords={totalRecords}
            />
          </div>
        </>
      ) : activeView === "client-followup-list" ? (
        <div className="p-24 bg-neutral-50 min-vh-100">
          <div
            className="p-24 radius-16 mb-24 position-relative overflow-hidden shadow-sm"
            style={{
              background: "linear-gradient(to right, #000000, #bb0007, #ed1c24)",
            }}
          >
            <div className="d-flex align-items-center justify-content-between position-relative z-1 flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-white w-40-px h-40-px rounded d-flex align-items-center justify-content-center shadow-sm border-0 bg-white"
                  onClick={() => setActiveView("table")}
                >
                  <Icon
                    icon="solar:alt-arrow-left-linear"
                    className="text-xl text-dark"
                  />
                </button>
                <div>
                  <div className="d-flex align-items-center gap-3 mb-4">
                    <h5 className="mb-0 fw-bold text-white">
                      {typeof (selectedExpo?.expoName || selectedExpo?.name) === "object"
                        ? (selectedExpo?.expoName || selectedExpo?.name)?.name
                        : selectedExpo?.expoName || selectedExpo?.name}
                    </h5>
                    <span className="badge bg-white text-danger-600 px-12 py-6 radius-8 fw-bold text-uppercase">
                      Block / Booking
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-4 text-white opacity-80 text-sm">
                    <div className="d-flex align-items-center gap-2">
                      <Icon icon="solar:map-point-bold" />
                      <span>{selectedExpo?.venue || "N/A"}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Icon icon="solar:calendar-date-bold" />
                      <span>
                        {selectedExpo?.startDate
                          ? `${formatDate(selectedExpo.startDate)} - ${formatDate(selectedExpo.endDate)}`
                          : ""}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card radius-16 border-0 shadow-sm mb-24 p-24 bg-white">
            <div className="row align-items-center gy-3">
              <div className="col-md-4">
                <label className="form-label fw-bold text-secondary-light small uppercase">Search Lead</label>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control radius-8 bg-neutral-50 h-40-px border-neutral-200"
                    placeholder="Search by Company, Person, Mobile..."
                    value={clientSearchInput}
                    onChange={(e) => handleClientFollowupSearch(e.target.value)}
                    style={{ paddingLeft: "40px" }}
                  />
                  <Icon
                    icon="ion:search-outline"
                    className="position-absolute text-secondary-light text-xl"
                    style={{ left: "12px", top: "50%", transform: "translateY(-50%)" }}
                  />
                </div>
              </div>

              <div className="col-md-3">
                <label className="form-label fw-bold text-secondary-light small uppercase">Filter by Manager</label>
                <Select
                  options={[
                    { value: "", label: "All Managers" },
                    ...((Array.isArray(selectedExpo?.eventManager)
                      ? selectedExpo.eventManager
                      : selectedExpo?.eventManager?.name
                        ? [selectedExpo.eventManager]
                        : []
                    ).map((m) => ({ value: m._id || m.id, label: m.name })))
                  ]}
                  value={{
                    value: selectedManagerFilter || "",
                    label: selectedManagerFilter
                      ? (Array.isArray(selectedExpo?.eventManager)
                        ? selectedExpo.eventManager
                        : selectedExpo?.eventManager?.name
                          ? [selectedExpo.eventManager]
                          : []
                      ).find((m) => (m._id || m.id) === selectedManagerFilter)?.name || "All Managers"
                      : "All Managers",
                  }}
                  onChange={(sel) => setSelectedManagerFilter(sel.value || null)}
                  styles={selectStyles()}
                />
              </div>

              {/* <div className="col-md-5 text-end align-self-end">
                <button
                  className="btn btn-danger radius-8 px-24 h-40-px text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                  style={{ backgroundColor: "#ed1c24", borderColor: "#ed1c24" }}
                  onClick={() => {
                    fetchExpoStalls("");
                    setNewEnquiryData({
                      name: "",
                      companyName: "",
                      mobileNo: "",
                      email: "",
                      category: selectedExpo?.category?._id || selectedExpo?.category || "",
                      interestedProduct: [],
                      segment: "B2B",
                      designation: "",
                      interestedLocations: [],
                      address: "",
                      city: "",
                      state: "",
                      pincode: "",
                      status: "Booked",
                      remark: "",
                      followUpDate: "",
                      stallNo: "",
                      sqft: "",
                      sqm: "",
                      totalAmount: "",
                      amountPaid: "",
                      stallType: "",
                      manualCost: "",
                      paymentMethod: "",
                      paymentStatus: "",
                      paymentScreenshot: null,
                      discount: 0,
                    });
                    setBookingCreatorType("Myself");
                    setBookingCreatorManagerId("");
                    setShowDirectBookingModal(true);
                  }}
                >
                  <Icon icon="solar:calendar-add-bold" className="text-lg" />
                  Book a Stall
                </button>
              </div> */}
            </div>
          </div>

          <div className="card radius-16 border-0 shadow-sm overflow-hidden bg-white">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0" style={{ minWidth: "1500px" }}>
                  <thead style={{ background: "linear-gradient(135deg, #bb0007, #ed1c24)" }}>
                    <tr>
                      <th className="py-16 px-20 text-xs fw-bold text-white uppercase border-0" style={{ width: "60px" }}>Sl.No</th>
                      <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "200px" }}>Company Name</th>
                      <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "160px" }}>Key Person</th>
                      <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "130px" }}>Mobile</th>
                      <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "100px" }}>Stall No</th>
                      <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "150px" }}>Manager</th>
                      <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "180px" }}>Blocked / Booked Date</th>
                      <th className="py-16 text-xs fw-bold text-white uppercase border-0 text-center" style={{ width: "130px" }}>Payment Status</th>
                      <th className="py-16 text-xs fw-bold text-white uppercase border-0 text-center" style={{ width: "130px" }}>Status</th>
                      <th className="py-16 text-xs fw-bold text-white uppercase border-0 text-end" style={{ width: "120px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientFollowupsLoading ? (
                      <tr>
                        <td colSpan="10" className="text-center py-80">
                          <div className="spinner-border text-danger" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    ) : clientFollowupsList.length > 0 ? (
                      clientFollowupsList.map((item, idx) => (
                        <tr key={item._id || idx}>
                          <td className="py-16 px-20 text-sm fw-semibold text-dark">
                            {clientFollowupsPage * clientFollowupsLimit + idx + 1}
                          </td>
                          <td className="py-16 text-sm fw-bold text-dark">
                            {item.companyName || item.client?.companyName || "N/A"}
                          </td>
                          <td className="py-16 text-sm text-dark">
                            {item.name || item.client?.keyPerson || "N/A"}
                          </td>
                          <td className="py-16 text-sm text-dark">
                            {item.mobileNo || item.client?.mobile1 || "N/A"}
                          </td>
                          <td className="py-16 text-sm text-dark fw-bold">
                            {(() => {
                              if (!item.statusDetails || item.statusDetails.length === 0) {
                                return item.stallNo || "N/A";
                              }
                              const blockedBookedStalls = item.statusDetails
                                .filter((d) => d.status === "Blocked" || d.status === "Block" || d.status === "Booked")
                                .map((d) => d.stallNo)
                                .filter(Boolean);
                              return blockedBookedStalls.length > 0
                                ? Array.from(new Set(blockedBookedStalls)).join(", ")
                                : item.stallNo || "N/A";
                            })()}
                          </td>
                          <td className="py-16 text-sm text-dark">
                            {item.manager?.name || item.managerId?.name || item.managerName || "N/A"}
                          </td>
                          <td className="py-16 text-sm text-dark">
                            {item.status === "Booked"
                              ? item.bookedTime
                                ? formatDateTime(item.bookedTime)
                                : item.updatedAt
                                  ? formatDateTime(item.updatedAt)
                                  : "—"
                              : item.blockedDate
                                ? formatDate(item.blockedDate)
                                : item.createdAt
                                  ? formatDate(item.createdAt)
                                  : "—"}
                          </td>
                          <td className="py-16 text-sm text-center">
                            <span
                              className={`badge px-10 py-6 radius-4 fw-bold uppercase ${item.paymentStatus === "Paid"
                                ? "bg-success-100 text-success-800"
                                : item.paymentStatus === "Partially Paid"
                                  ? "bg-warning-100 text-warning-800"
                                  : "bg-danger-100 text-danger-800"
                                }`}
                            >
                              {item.paymentStatus || "Unpaid"}
                            </span>
                          </td>
                          <td className="py-16 text-sm text-center">
                            <span
                              className={`badge px-10 py-6 radius-4 fw-bold uppercase ${item.approvalStatus === "Approved"
                                ? "bg-success-100 text-success-800"
                                : item.approvalStatus === "Rejected"
                                  ? "bg-danger-100 text-danger-800"
                                  : "bg-warning-100 text-warning-800"
                                }`}
                            >
                              {item.approvalStatus || "Pending"}
                            </span>
                          </td>
                          <td className="py-16 text-end">
                            <div className="d-flex align-items-center justify-content-end gap-2 px-16">
                              <button
                                type="button"
                                className="fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                style={{ backgroundColor: "#eef2ff", color: "#4338ca", border: "1px solid #e0e7ff" }}
                                title="View Full Details"
                                onClick={async () => {
                                  setSelectedBlockItem(item);
                                  setShowBlockDetailModal(true);
                                  const res = await getClientFollowupPayments(item._id);
                                  if (res.status) {
                                    setSelectedBlockPayments(res.response?.data || res.response || []);
                                  } else {
                                    setSelectedBlockPayments([]);
                                  }
                                }}
                              >
                                <Icon icon="lucide:eye" className="text-xl" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-40 text-secondary-light">
                          No blocked or booked stalls found for this past expo.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="mt-24">
            <TablePagination
              currentPage={clientFollowupsPage}
              totalPages={Math.ceil(clientFollowupsTotal / clientFollowupsLimit) || 1}
              onPageChange={setClientFollowupsPage}
              rowsPerPage={clientFollowupsLimit}
              onRowsPerPageChange={(e) => {
                setClientFollowupsLimit(parseInt(e.target.value));
                setClientFollowupsPage(0);
              }}
              totalRecords={clientFollowupsTotal}
            />
          </div>
        </div>
      ) : null}

      {/* Direct Stall Booking Modal */}
      {showDirectBookingModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content radius-16 border-0 shadow-lg" style={{ maxHeight: "90vh", overflowY: "auto" }}>
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{ background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)" }}
              >
                <h6 className="modal-title text-white mb-0 d-flex align-items-center gap-2">
                  <Icon icon="solar:calendar-add-bold" className="text-lg" />
                  Direct Stall Booking Form (Past Expo)
                </h6>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowDirectBookingModal(false)} />
              </div>
              <div className="modal-body p-24 text-start">
                <form onSubmit={handleDirectBookingSubmit} className="row g-3 text-xs">
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Mobile Number *</label>
                    <input
                      type="text"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter 10-digit mobile number"
                      maxLength="10"
                      required
                      value={newEnquiryData.mobileNo}
                      onChange={handleMobileChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Key Person Name *</label>
                    <input
                      type="text"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter Key Person Name"
                      required
                      value={newEnquiryData.name}
                      onChange={(e) => setNewEnquiryData({ ...newEnquiryData, name: e.target.value })}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Company Name *</label>
                    <input
                      type="text"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter Company Name"
                      required
                      value={newEnquiryData.companyName}
                      onChange={(e) => setNewEnquiryData({ ...newEnquiryData, companyName: e.target.value })}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Designation</label>
                    <input
                      type="text"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter Designation"
                      value={newEnquiryData.designation || ""}
                      onChange={(e) => setNewEnquiryData({ ...newEnquiryData, designation: e.target.value })}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Email Address</label>
                    <input
                      type="email"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter Email"
                      value={newEnquiryData.email || ""}
                      onChange={(e) => setNewEnquiryData({ ...newEnquiryData, email: e.target.value })}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">City</label>
                    <input
                      type="text"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter City"
                      value={newEnquiryData.city || ""}
                      onChange={(e) => setNewEnquiryData({ ...newEnquiryData, city: e.target.value.replace(/[0-9]/g, "") })}
                    />
                  </div>

                  <div className="col-md-12">
                    <label className="form-label fw-bold text-secondary-light">Products</label>
                    <Select
                      isMulti
                      options={productOptions}
                      value={productOptions.filter((opt) => newEnquiryData.interestedProduct?.includes(opt.value))}
                      onChange={(selected) => {
                        const values = selected ? selected.map((opt) => opt.value) : [];
                        setNewEnquiryData({ ...newEnquiryData, interestedProduct: values });
                      }}
                      styles={selectStyles()}
                      placeholder="Select Products..."
                    />
                  </div>

                  <div className="col-md-12">
                    <label className="form-label fw-bold text-secondary-light">Interested Locations</label>
                    <Select
                      isMulti
                      options={locationOptions}
                      value={locationOptions.filter((opt) => newEnquiryData.interestedLocations?.includes(opt.value))}
                      onChange={(selected) => {
                        const values = selected ? selected.map((opt) => opt.value) : [];
                        setNewEnquiryData({ ...newEnquiryData, interestedLocations: values });
                      }}
                      styles={selectStyles()}
                      placeholder="Select Locations..."
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Created By *</label>
                    <Select
                      options={[
                        { value: "Myself", label: "Myself" },
                        { value: "Manager", label: "Manager" },
                        { value: "Overall", label: "Overall" },
                      ]}
                      value={{ value: bookingCreatorType, label: bookingCreatorType }}
                      onChange={(sel) => {
                        setBookingCreatorType(sel.value);
                        setBookingCreatorManagerId("");
                      }}
                      styles={selectStyles()}
                    />
                  </div>

                  {(bookingCreatorType === "Manager" || bookingCreatorType === "Overall") && (
                    <div className="col-md-6">
                      <label className="form-label fw-bold text-secondary-light">Select Manager *</label>
                      <Select
                        options={(() => {
                          if (bookingCreatorType === "Overall") {
                            return managersList.map((m) => ({
                              value: m.value || m._id || m.id,
                              label: m.label || m.name,
                            }));
                          }
                          const managers = Array.isArray(selectedExpo?.eventManager)
                            ? selectedExpo.eventManager
                            : selectedExpo?.eventManager?.name
                              ? [selectedExpo.eventManager]
                              : [];
                          return managers.map((m) => ({
                            value: m._id || m.id,
                            label: m.name,
                          }));
                        })()}
                        value={(() => {
                          let optionsList = [];
                          if (bookingCreatorType === "Overall") {
                            optionsList = managersList.map((m) => ({
                              value: m.value || m._id || m.id,
                              label: m.label || m.name,
                            }));
                          } else {
                            const managers = Array.isArray(selectedExpo?.eventManager)
                              ? selectedExpo.eventManager
                              : selectedExpo?.eventManager?.name
                                ? [selectedExpo.eventManager]
                                : [];
                            optionsList = managers.map((m) => ({
                              value: m._id || m.id,
                              label: m.name,
                            }));
                          }
                          const selectedMgr = optionsList.find((m) => m.value === bookingCreatorManagerId);
                          return selectedMgr || null;
                        })()}
                        onChange={(sel) => setBookingCreatorManagerId(sel?.value || "")}
                        placeholder="-- Select Manager --"
                        isSearchable
                        styles={selectStyles()}
                      />
                    </div>
                  )}

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Stall Number *</label>
                    {(() => {
                      const availableStalls = stallOptions.filter((opt) => opt.status !== "Blocked" && opt.status !== "Booked");
                      return (
                        <Select
                          options={availableStalls}
                          value={stallOptions.find((opt) => opt.value === newEnquiryData.stallNo)}
                          onChange={(sel) => {
                            const sqm = sel?.sqm || 0;
                            const totalAmount = sel?.withgstvalue || sel?.stallValue || 0;
                            setNewEnquiryData({
                              ...newEnquiryData,
                              stallNo: sel?.value || "",
                              sqm: sqm,
                              totalAmount: totalAmount,
                              stallType: sel?.type || "",
                            });
                          }}
                          placeholder="Search & Select Stall"
                          isSearchable
                          styles={selectStyles()}
                        />
                      );
                    })()}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Stall Type</label>
                    <input
                      type="text"
                      className="form-control radius-8 bg-light form-control-sm"
                      placeholder="Auto fetch"
                      value={stallTypeOptions.find((opt) => opt.value === newEnquiryData.stallType)?.label || ""}
                      readOnly
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Sqm</label>
                    <input
                      type="text"
                      className="form-control radius-8 bg-light form-control-sm"
                      placeholder="Auto fetch"
                      value={newEnquiryData.sqm || ""}
                      readOnly
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Tariff Rate (with GST)</label>
                    <input
                      type="text"
                      className="form-control radius-8 bg-light form-control-sm"
                      placeholder="Auto fetch"
                      value={formatIndianNumber(newEnquiryData.totalAmount)}
                      readOnly
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Final Amount (After Discount) *</label>
                    <input
                      type="text"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter net price to collect"
                      required
                      value={newEnquiryData.discount || ""}
                      onChange={(e) => {
                        const rawVal = e.target.value.replace(/[^0-9.]/g, "");
                        setNewEnquiryData({
                          ...newEnquiryData,
                          discount: rawVal,
                        });
                      }}
                    />
                    {newEnquiryData.discount && newEnquiryData.totalAmount && (
                      <div className="text-xs text-secondary-light mt-1">
                        Discount off: ₹{formatIndianNumber(parseFloat((parseFloat(newEnquiryData.totalAmount) - parseFloat(newEnquiryData.discount)).toFixed(2)))}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Remarks</label>
                    <textarea
                      className="form-control radius-8 form-control-sm"
                      rows="2"
                      placeholder="Remarks / Comments"
                      value={newEnquiryData.remark || ""}
                      onChange={(e) => setNewEnquiryData({ ...newEnquiryData, remark: e.target.value })}
                    />
                  </div>

                  <div className="col-12 text-end border-top pt-16">
                    <button
                      type="button"
                      className="btn btn-secondary radius-8 px-24 me-2"
                      onClick={() => setShowDirectBookingModal(false)}
                      disabled={directBookingSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-danger radius-8 px-24 text-white fw-bold shadow-sm"
                      style={{ backgroundColor: "#ed1c24", borderColor: "#ed1c24" }}
                      disabled={directBookingSubmitting}
                    >
                      {directBookingSubmitting ? "Booking..." : "Book Stall"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block Details & Payment History Modal (100% Identical to PresentExpo) */}
      {showBlockDetailModal && selectedBlockItem && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content radius-16 border-0 shadow-lg" style={{ maxHeight: "90vh", overflowY: "auto" }}>
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{ background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)" }}
              >
                <h6 className="modal-title text-white mb-0 d-flex align-items-center gap-2">
                  <Icon icon="solar:lock-keyhole-bold" className="text-lg" />
                  Stall Booking Details — {selectedBlockItem.companyName || selectedBlockItem.client?.companyName}
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowBlockDetailModal(false);
                    setSelectedBlockItem(null);
                    setSelectedBlockPayments([]);
                  }}
                />
              </div>
              <div className="modal-body p-24">
                <div
                  className={`alert mb-20 py-12 px-16 radius-12 d-flex align-items-center gap-3 border-0 ${selectedBlockItem.approvalStatus === "Approved"
                    ? "bg-success-100"
                    : selectedBlockItem.approvalStatus === "Rejected"
                      ? "bg-danger-100"
                      : "bg-warning-100"
                    }`}
                >
                  <Icon
                    icon={
                      selectedBlockItem.approvalStatus === "Approved"
                        ? "solar:check-circle-bold"
                        : selectedBlockItem.approvalStatus === "Rejected"
                          ? "solar:close-circle-bold"
                          : "solar:clock-circle-bold"
                    }
                    className={`text-xl ${selectedBlockItem.approvalStatus === "Approved"
                      ? "text-success-600"
                      : selectedBlockItem.approvalStatus === "Rejected"
                        ? "text-danger-600"
                        : "text-warning-600"
                      }`}
                  />
                  <div>
                    <div
                      className={`fw-bold text-sm ${selectedBlockItem.approvalStatus === "Approved"
                        ? "text-success-800"
                        : selectedBlockItem.approvalStatus === "Rejected"
                          ? "text-danger-800"
                          : "text-warning-800"
                        }`}
                    >
                      Booking Status: {selectedBlockItem.approvalStatus || "Pending"}
                    </div>
                    {selectedBlockItem.remarks && <div className="text-xs text-secondary-light mt-1">{selectedBlockItem.remarks}</div>}
                  </div>
                </div>

                <div className="row gy-3">
                  <div className="col-12">
                    <h6 className="text-xs fw-bold text-secondary-light uppercase mb-12 border-bottom pb-8">Company Information</h6>
                  </div>
                  <div className="col-md-4">
                    <div className="text-xs text-secondary-light fw-bold mb-4">Company Name</div>
                    <div className="text-sm fw-bold text-dark">{selectedBlockItem.companyName || selectedBlockItem.client?.companyName || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-xs text-secondary-light fw-bold mb-4">Key Person</div>
                    <div className="text-sm text-dark">{selectedBlockItem.name || selectedBlockItem.client?.keyPerson || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-xs text-secondary-light fw-bold mb-4">Mobile</div>
                    <div className="text-sm text-dark">{selectedBlockItem.mobileNo || selectedBlockItem.client?.mobile1 || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-xs text-secondary-light fw-bold mb-4">Email</div>
                    <div className="text-sm text-dark">{selectedBlockItem.email || selectedBlockItem.client?.email || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-xs text-secondary-light fw-bold mb-4">Designation</div>
                    <div className="text-sm text-dark">{selectedBlockItem.designation || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-xs text-secondary-light fw-bold mb-4">Manager</div>
                    <div className="text-sm text-dark">{selectedBlockItem.manager?.name || "N/A"}</div>
                  </div>

                  <div className="col-12 mt-8">
                    <h6 className="text-xs fw-bold text-secondary-light uppercase mb-12 border-bottom pb-8">Stall Blocks & Financial Details</h6>
                  </div>

                  <div className="col-12">
                    <div className="d-flex flex-column gap-3">
                      {(() => {
                        const blockDetails = (selectedBlockItem.statusDetails || []).filter(
                          (d) => d.status === "Blocked" || d.status === "Block" || d.status === "Booked"
                        );

                        const uniqueStallBlocks = {};
                        blockDetails.forEach((d) => {
                          if (d.stallNo) {
                            if (!uniqueStallBlocks[d.stallNo]) {
                              uniqueStallBlocks[d.stallNo] = { ...d };
                            } else {
                              const existing = uniqueStallBlocks[d.stallNo];
                              uniqueStallBlocks[d.stallNo] = {
                                ...existing,
                                status: d.status || existing.status,
                                approvalStatus: d.approvalStatus || existing.approvalStatus,
                                bookedTime: d.bookedTime || existing.bookedTime,
                                remarks: d.remarks || existing.remarks,
                                sqm: d.sqm || existing.sqm,
                                stallType: d.stallType || existing.stallType,
                                totalAmount: d.totalAmount || existing.totalAmount,
                                discount: d.discount !== undefined && d.discount !== null ? d.discount : existing.discount,
                                amount: d.amount || existing.amount,
                                blockedDate: d.blockedDate || existing.blockedDate,
                              };
                            }
                          }
                        });

                        const uniqueStallList = Object.values(uniqueStallBlocks);
                        const finalStallList =
                          uniqueStallList.length > 0
                            ? uniqueStallList
                            : [
                              {
                                stallNo: selectedBlockItem.stallNo,
                                stallType: selectedBlockItem.stallType,
                                sqm: selectedBlockItem.sqm,
                                totalAmount: selectedBlockItem.totalAmount,
                                discount: selectedBlockItem.discount,
                                amount: selectedBlockItem.amount,
                                approvalStatus: selectedBlockItem.approvalStatus || "Pending",
                                blockedDate: selectedBlockItem.blockedDate,
                                bookedTime: selectedBlockItem.bookedTime,
                              },
                            ];

                        return finalStallList.map((stallBlock, sIdx) => {
                          const displayApprovalStatus = stallBlock.approvalStatus || "Pending";
                          const isCurrentlyEditing = editingStallNo === stallBlock.stallNo;
                          const totalPaidAmount = (selectedBlockPayments || [])
                            .filter((p) => p.paymentStatus === "Completed")
                            .reduce((sum, p) => sum + (Number(p.amountPaid) || 0), 0);

                          return (
                            <div key={sIdx} className="p-16 border radius-12 bg-light shadow-sm">
                              <div className="d-flex justify-content-between align-items-center mb-12 flex-wrap gap-2 pb-8 border-bottom">
                                <div className="d-flex align-items-center gap-2">
                                  <Icon icon="solar:widget-5-bold" className="text-primary-600 text-lg" />
                                  <span className="fw-bold text-dark text-md">Stall {stallBlock.stallNo || "N/A"}</span>
                                  <span
                                    className={`badge px-10 py-6 radius-4 text-xs uppercase ${displayApprovalStatus === "Approved"
                                      ? "bg-success-100 text-success-800"
                                      : displayApprovalStatus === "Rejected"
                                        ? "bg-danger-100 text-danger-800"
                                        : "bg-warning-100 text-warning-800"
                                      }`}
                                  >
                                    {displayApprovalStatus}
                                  </span>
                                </div>
                                <div className="d-flex gap-2">
                                  {isCurrentlyEditing ? (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-sm px-16 radius-6 text-white fw-bold d-flex align-items-center gap-1"
                                        style={{ backgroundColor: "#1d4ed8", fontSize: "12px" }}
                                        disabled={editSaving}
                                        onClick={async () => {
                                          if (!editAmount || isNaN(Number(editAmount))) {
                                            toast.error("Please enter a valid net amount");
                                            return;
                                          }
                                          setEditSaving(true);
                                          const formattedAddons = editAddons
                                            .filter((a) => (a.title && a.title.trim() !== "") || Number(a.amount) > 0)
                                            .map((a) => {
                                              const amt = Number(a.amount) || 0;
                                              const gst = a.hasGst ? Math.round(amt * 0.18) : 0;
                                              return {
                                                title: (a.title || "").trim() || "Add-on",
                                                amount: amt,
                                                gst: gst,
                                                total: amt + gst,
                                              };
                                            });

                                          const res = await editStallFinancials({
                                            followupId: selectedBlockItem._id,
                                            stallNo: stallBlock.stallNo,
                                            amount: Number(editAmount),
                                            remarks: editRemarks,
                                            addons: formattedAddons,
                                          });
                                          setEditSaving(false);
                                          if (res.status) {
                                            toast.success("Stall financials updated successfully");
                                            setEditingStallNo(null);
                                            const updatedFollowup = res.response?.data || res.response;
                                            if (updatedFollowup) {
                                              setSelectedBlockItem(updatedFollowup);
                                            }
                                            fetchClientFollowupsList(
                                              selectedExpo?._id || selectedExpo?.id,
                                              clientFollowupStatus,
                                              clientFollowupsPage,
                                              clientFollowupsLimit,
                                              clientFollowupSearchTerm,
                                              selectedManagerFilter
                                            );
                                          } else {
                                            toast.error(res.response?.message || "Failed to update stall financials");
                                          }
                                        }}
                                      >
                                        {editSaving ? "Saving..." : "Save"}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm px-16 radius-6 text-white fw-bold d-flex align-items-center gap-1 btn-secondary"
                                        style={{ fontSize: "12px" }}
                                        disabled={editSaving}
                                        onClick={() => setEditingStallNo(null)}
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      {/* <button
                                        type="button"
                                        className="btn btn-sm px-16 radius-6 text-white fw-bold d-flex align-items-center gap-1"
                                        style={{ backgroundColor: "#0284c7", fontSize: "12px" }}
                                        onClick={() => {
                                          setEditingStallNo(stallBlock.stallNo);
                                          const currentAddons =
                                            stallBlock.addons && stallBlock.addons.length > 0
                                              ? stallBlock.addons
                                              : selectedBlockItem.addons || [];
                                          const addonBase = currentAddons.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
                                          const addonGst = currentAddons.reduce(
                                            (sum, a) => sum + (Number(a.gst) !== undefined ? Number(a.gst) : 0),
                                            0
                                          );
                                          const addonTotal = addonBase + addonGst;
                                          const overallTotal = Number(stallBlock.amount) || 0;
                                          const stallTotalWithGst = Math.max(0, overallTotal - addonTotal);
                                          const initialStallBase = stallTotalWithGst > 0 ? Math.round(stallTotalWithGst / 1.18) : 0;

                                          setEditAmount(initialStallBase || "");
                                          setEditRemarks(stallBlock.remarks || "");
                                          setEditAddons(
                                            currentAddons.map((a) => ({
                                              title: a.title || "",
                                              amount: a.amount !== undefined ? a.amount : "",
                                              hasGst: a.gst !== undefined ? Number(a.gst) > 0 : false,
                                            }))
                                          );
                                        }}
                                      >
                                        <Icon icon="solar:pen-bold" /> Edit Financials
                                      </button> */}

                                      {displayApprovalStatus === "Pending" && (
                                        <>
                                          <button
                                            type="button"
                                            className="btn btn-sm px-16 radius-6 text-white fw-bold d-flex align-items-center gap-1"
                                            style={{ backgroundColor: "#dc2626", fontSize: "12px" }}
                                            onClick={async () => {
                                              const res = await updateClientFollowupStatus({
                                                followupId: selectedBlockItem._id,
                                                clientId: selectedBlockItem.clientId?._id || selectedBlockItem.clientId?.id || selectedBlockItem.clientId,
                                                expoId: selectedExpo?._id || selectedExpo?.id,
                                                status: "Blocked",
                                                approvalStatus: "Rejected",
                                                remarks: "Booking Rejected by Admin",
                                              });
                                              if (res.status) {
                                                toast.success("Booking Rejected");
                                                setSelectedBlockItem((prev) => ({ ...prev, approvalStatus: "Rejected" }));
                                                fetchClientFollowupsList(
                                                  selectedExpo?._id || selectedExpo?.id,
                                                  clientFollowupStatus,
                                                  clientFollowupsPage,
                                                  clientFollowupsLimit,
                                                  clientFollowupSearchTerm,
                                                  selectedManagerFilter
                                                );
                                              }
                                            }}
                                          >
                                            <Icon icon="solar:close-circle-bold" /> Reject
                                          </button>

                                          <button
                                            type="button"
                                            className="btn btn-sm px-16 radius-6 text-white fw-bold d-flex align-items-center gap-1"
                                            style={{ backgroundColor: "#16a34a", fontSize: "12px" }}
                                            onClick={async () => {
                                              const res = await updateClientFollowupStatus({
                                                followupId: selectedBlockItem._id,
                                                clientId: selectedBlockItem.clientId?._id || selectedBlockItem.clientId?.id || selectedBlockItem.clientId,
                                                expoId: selectedExpo?._id || selectedExpo?.id,
                                                status: "Blocked",
                                                approvalStatus: "Approved",
                                                remarks: "Booking Approved by Admin",
                                              });
                                              if (res.status) {
                                                toast.success("Booking Approved Successfully!");
                                                setSelectedBlockItem((prev) => ({ ...prev, approvalStatus: "Approved" }));
                                                fetchClientFollowupsList(
                                                  selectedExpo?._id || selectedExpo?.id,
                                                  clientFollowupStatus,
                                                  clientFollowupsPage,
                                                  clientFollowupsLimit,
                                                  clientFollowupSearchTerm,
                                                  selectedManagerFilter
                                                );
                                              }
                                            }}
                                          >
                                            <Icon icon="solar:check-circle-bold" /> Approve
                                          </button>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>

                              <div className="row gy-2 text-xs">
                                <div className="col-md-3">
                                  <span className="text-secondary-light fw-bold">Stall Type:</span>{" "}
                                  <span className="text-dark fw-semibold">
                                    {typeof stallBlock.stallType === "object"
                                      ? stallBlock.stallType?.stallTypeName
                                      : stallTypeOptions.find((o) => o.value === stallBlock.stallType)?.label ||
                                      stallBlock.stallType ||
                                      "N/A"}
                                  </span>
                                </div>
                                <div className="col-md-3">
                                  <span className="text-secondary-light fw-bold">Size:</span>{" "}
                                  <span className="text-dark fw-semibold">{stallBlock.sqm || 0} sqm</span>
                                </div>
                                <div className="col-md-3">
                                  <span className="text-secondary-light fw-bold">Tariff:</span>{" "}
                                  <span className="text-dark fw-semibold">₹{formatIndianNumber(stallBlock.totalAmount || 0)}</span>
                                </div>
                                <div className="col-md-3">
                                  <span className="text-secondary-light fw-bold">Discount:</span>{" "}
                                  <span className="text-danger fw-semibold">
                                    ₹{
                                      (() => {
                                        if (!isCurrentlyEditing) return formatIndianNumber(stallBlock.discount || 0);
                                        const activeAddonsForDisc = editAddons.map((a) => ({
                                          amount: Number(a.amount) || 0,
                                          gst: a.hasGst ? Math.round((Number(a.amount) || 0) * 0.18) : 0,
                                        }));
                                        const addBaseDisc = activeAddonsForDisc.reduce((s, a) => s + a.amount, 0);
                                        const addGstDisc = activeAddonsForDisc.reduce((s, a) => s + a.gst, 0);
                                        const stBaseDisc = Number(editAmount) || 0;
                                        const stGstDisc = Math.round(stBaseDisc * 0.18);
                                        const currentEditNetTotal = stBaseDisc + stGstDisc + addBaseDisc + addGstDisc;
                                        return formatIndianNumber(Math.max(0, (stallBlock.totalAmount || 0) - currentEditNetTotal));
                                      })()
                                    }
                                  </span>
                                </div>

                                <div className="col-md-3 d-flex align-items-center gap-1">
                                  <span className="text-secondary-light fw-bold">Requested Base Rate:</span>
                                  {isCurrentlyEditing ? (
                                    <input
                                      type="number"
                                      className="form-control px-8 py-2 text-xs"
                                      style={{ width: "90px", height: "24px" }}
                                      value={editAmount}
                                      onChange={(e) => setEditAmount(e.target.value)}
                                    />
                                  ) : (
                                    <span className="text-success fw-bold">
                                      ₹{formatIndianNumber(
                                        (() => {
                                          const currentAddons =
                                            stallBlock.addons && stallBlock.addons.length > 0
                                              ? stallBlock.addons
                                              : selectedBlockItem.addons || [];
                                          const addonBase = currentAddons.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
                                          const addonGst = currentAddons.reduce(
                                            (sum, a) =>
                                              sum +
                                              (Number(a.gst) !== undefined
                                                ? Number(a.gst)
                                                : Math.round((Number(a.amount) || 0) * 0.18)),
                                            0
                                          );
                                          const addonTotal = addonBase + addonGst;
                                          const overallTotal = Number(stallBlock.amount) || 0;
                                          const stallTotalWithGst = Math.max(0, overallTotal - addonTotal);
                                          return stallTotalWithGst > 0 ? Math.round(stallTotalWithGst / 1.18) : 0;
                                        })()
                                      )}
                                    </span>
                                  )}
                                </div>

                                <div className="col-md-3">
                                  <span className="text-secondary-light fw-bold">Blocked Date:</span>{" "}
                                  <span className="text-dark">
                                    {stallBlock.blockedDate
                                      ? formatDate(stallBlock.blockedDate)
                                      : selectedBlockItem.createdAt
                                        ? formatDate(selectedBlockItem.createdAt)
                                        : "—"}
                                  </span>
                                </div>
                                {stallBlock.bookedTime && (
                                  <div className="col-md-3">
                                    <span className="text-secondary-light fw-bold">Booked Time:</span>{" "}
                                    <span className="text-dark">{formatDateTime(stallBlock.bookedTime)}</span>
                                  </div>
                                )}

                                {/* Financial Summary Box */}
                                {(() => {
                                  const baseRate = selectedExpo?.sqmRate || 0;
                                  const rateWithGst = Math.round(baseRate * 1.18);

                                  const activeAddons = isCurrentlyEditing
                                    ? editAddons.map((a) => {
                                      const amt = Number(a.amount) || 0;
                                      const gst = a.hasGst ? Math.round(amt * 0.18) : 0;
                                      return { title: a.title || "", amount: amt, gst: gst, total: amt + gst };
                                    })
                                    : stallBlock.addons && stallBlock.addons.length > 0
                                      ? stallBlock.addons
                                      : selectedBlockItem.addons || [];

                                  const addonBaseTotal = activeAddons.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
                                  const addonGstTotal = activeAddons.reduce(
                                    (sum, a) =>
                                      sum +
                                      (Number(a.gst) !== undefined
                                        ? Number(a.gst)
                                        : Math.round((Number(a.amount) || 0) * 0.18)),
                                    0
                                  );
                                  const addonTotalWithGst = addonBaseTotal + addonGstTotal;

                                  const hasAddons = activeAddons.length > 0;

                                  let stallBaseAmount = 0;
                                  let stallGst = 0;
                                  let stallTotalWithGst = 0;

                                  if (isCurrentlyEditing) {
                                    stallBaseAmount = Number(editAmount) || 0;
                                    stallGst = Math.round(stallBaseAmount * 0.18);
                                    stallTotalWithGst = stallBaseAmount + stallGst;
                                  } else {
                                    const overallTotal = Number(stallBlock.amount) || 0;
                                    stallTotalWithGst = Math.max(0, overallTotal - addonTotalWithGst);
                                    stallBaseAmount = stallTotalWithGst > 0 ? Math.round(stallTotalWithGst / 1.18) : 0;
                                    stallGst = Math.max(0, stallTotalWithGst - stallBaseAmount);
                                  }

                                  const sqmSize = Number(stallBlock.sqm) || 0;
                                  const managerRatePerSqm = sqmSize > 0 ? Math.round(stallBaseAmount / sqmSize) : 0;

                                  const grossAmount = stallBaseAmount + addonBaseTotal;
                                  const totalGst = stallGst + addonGstTotal;
                                  const finalTotalAmount = grossAmount + totalGst;

                                  return (
                                    <div className="col-12 mt-16">
                                      <div
                                        className="p-12 radius-8 d-flex flex-column gap-3"
                                        style={{ backgroundColor: "#f8fafc", border: "1px dashed #cbd5e1" }}
                                      >
                                        {/* Admin Reference Rates */}
                                        <div className="d-flex flex-wrap gap-4 align-items-center">
                                          <div className="d-flex align-items-center gap-2">
                                            <span className="text-secondary-light fw-bold">Admin Base Rate/sqm:</span>
                                            <span className="text-dark fw-bold">₹{formatIndianNumber(Math.round(baseRate))}</span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2 ms-md-4">
                                            <span className="text-secondary-light fw-bold">Admin Rate/sqm (+18% GST):</span>
                                            <span className="text-primary fw-bold">₹{formatIndianNumber(rateWithGst)}</span>
                                          </div>
                                        </div>

                                        {/* Stall Financial Breakdown */}
                                        <div className="pt-8 border-top d-flex flex-wrap gap-4 align-items-center">
                                          <div className="d-flex align-items-center gap-2">
                                            <span className="text-secondary-light fw-bold">Manager Rate/sqm:</span>
                                            <span className="text-dark fw-bold">₹{formatIndianNumber(managerRatePerSqm)}</span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2 ms-md-2">
                                            <span className="text-secondary-light fw-bold">Stall Base:</span>
                                            <span className="text-dark fw-bold">₹{formatIndianNumber(stallBaseAmount)}</span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2 ms-md-2">
                                            <span className="text-secondary-light fw-bold">Stall GST (18%):</span>
                                            <span className="text-warning-700 fw-bold">₹{formatIndianNumber(stallGst)}</span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2 ms-md-2">
                                            <span className="text-secondary-light fw-bold">Stall Total:</span>
                                            <span className="text-primary fw-bold">₹{formatIndianNumber(stallTotalWithGst)}</span>
                                          </div>
                                        </div>

                                        {/* Add-ons Financial Breakdown (if present) */}
                                        {hasAddons && (
                                          <div className="pt-6 border-top d-flex flex-wrap gap-4 align-items-center">
                                            <div className="d-flex align-items-center gap-2">
                                              <span className="text-secondary-light fw-bold">Add-ons Base:</span>
                                              <span className="text-dark fw-bold">₹{formatIndianNumber(addonBaseTotal)}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2 ms-md-2">
                                              <span className="text-secondary-light fw-bold">Add-ons GST (18%):</span>
                                              <span className="text-warning-700 fw-bold">₹{formatIndianNumber(addonGstTotal)}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2 ms-md-2">
                                              <span className="text-secondary-light fw-bold">Add-ons Total:</span>
                                              <span className="text-info-700 fw-bold">₹{formatIndianNumber(addonTotalWithGst)}</span>
                                            </div>
                                          </div>
                                        )}

                                        {/* Grand Overall Totals */}
                                        <div className="pt-8 border-top d-flex flex-wrap gap-4 align-items-center bg-white p-8 radius-6">
                                          <div className="d-flex align-items-center gap-2">
                                            <span className="text-secondary-light fw-bold">Gross Amount (Base Total):</span>
                                            <span className="text-dark fw-bold">₹{formatIndianNumber(grossAmount)}</span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2 ms-md-3">
                                            <span className="text-secondary-light fw-bold">Total GST (18%):</span>
                                            <span className="text-warning-700 fw-bold">₹{formatIndianNumber(totalGst)}</span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2 ms-md-3">
                                            <span className="text-secondary-light fw-bold">Total Amount:</span>
                                            <span className="text-success-600 fw-bold fs-6">₹{formatIndianNumber(finalTotalAmount)}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* Add-ons View Mode Table */}
                                {!isCurrentlyEditing &&
                                  (() => {
                                    const currentAddons =
                                      stallBlock.addons && stallBlock.addons.length > 0
                                        ? stallBlock.addons
                                        : selectedBlockItem.addons || [];

                                    if (!currentAddons || currentAddons.length === 0) return null;

                                    const addonBaseSum = currentAddons.reduce(
                                      (sum, a) => sum + (Number(a.amount) || 0),
                                      0
                                    );
                                    const addonGstSum = currentAddons.reduce(
                                      (sum, a) =>
                                        sum +
                                        (Number(a.gst) !== undefined
                                          ? Number(a.gst)
                                          : Math.round((Number(a.amount) || 0) * 0.18)),
                                      0
                                    );
                                    const addonTotalSum = currentAddons.reduce(
                                      (sum, a) =>
                                        sum +
                                        (Number(a.total) !== undefined
                                          ? Number(a.total)
                                          : Math.round((Number(a.amount) || 0) * 1.18)),
                                      0
                                    );

                                    return (
                                      <div className="col-12 mt-12 pt-12 border-top">
                                        <div className="d-flex justify-content-between align-items-center mb-8">
                                          <span className="text-secondary-light fw-bold text-xs uppercase tracking-wider">
                                            <Icon icon="solar:box-minimalistic-bold" className="me-1 text-primary-600" />
                                            Add-ons ({currentAddons.length})
                                          </span>
                                        </div>
                                        <div className="table-responsive radius-8 border" style={{ backgroundColor: "#ffffff" }}>
                                          <table className="table table-sm text-xs mb-0">
                                            <thead className="bg-light">
                                              <tr>
                                                <th className="py-6 px-12 text-secondary-light fw-semibold">Add-on Item</th>
                                                <th className="py-6 px-12 text-end text-secondary-light fw-semibold">Base Price</th>
                                                <th className="py-6 px-12 text-end text-secondary-light fw-semibold">GST (18%)</th>
                                                <th className="py-6 px-12 text-end text-secondary-light fw-semibold">Total (with GST)</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {currentAddons.map((item, aIdx) => {
                                                const base = Number(item.amount) || 0;
                                                const gst =
                                                  Number(item.gst) !== undefined ? Number(item.gst) : Math.round(base * 0.18);
                                                const tot = Number(item.total) !== undefined ? Number(item.total) : base + gst;
                                                return (
                                                  <tr key={aIdx}>
                                                    <td className="py-6 px-12 text-dark fw-medium">{item.title || "Add-on Item"}</td>
                                                    <td className="py-6 px-12 text-end text-dark">₹{formatIndianNumber(base)}</td>
                                                    <td className="py-6 px-12 text-end text-warning-700">₹{formatIndianNumber(gst)}</td>
                                                    <td className="py-6 px-12 text-end text-success-600 fw-bold">₹{formatIndianNumber(tot)}</td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                            <tfoot className="bg-light border-top">
                                              <tr className="fw-bold">
                                                <td className="py-6 px-12 text-dark">Add-ons Subtotal</td>
                                                <td className="py-6 px-12 text-end text-dark">₹{formatIndianNumber(addonBaseSum)}</td>
                                                <td className="py-6 px-12 text-end text-warning-700">₹{formatIndianNumber(Math.round(addonGstSum))}</td>
                                                <td className="py-6 px-12 text-end text-success-600">₹{formatIndianNumber(Math.round(addonTotalSum))}</td>
                                              </tr>
                                            </tfoot>
                                          </table>
                                        </div>
                                      </div>
                                    );
                                  })()}

                                {/* Add-ons Edit Mode Management */}
                                {isCurrentlyEditing && (
                                  <div className="col-12 mt-12 pt-12 border-top">
                                    <div className="d-flex justify-content-between align-items-center mb-8">
                                      <span className="text-secondary-light fw-bold text-xs uppercase tracking-wider">
                                        <Icon icon="solar:box-minimalistic-bold" className="me-1 text-primary-600" />
                                        Manage Add-ons
                                      </span>
                                      <button
                                        type="button"
                                        className="btn btn-xs btn-outline-primary radius-4 d-flex align-items-center gap-1 py-1 px-8 text-xs fw-bold"
                                        onClick={() => {
                                          setEditAddons((prev) => [...prev, { title: "", amount: "", hasGst: false }]);
                                        }}
                                      >
                                        <Icon icon="solar:add-circle-bold" /> Add Add-on
                                      </button>
                                    </div>

                                    {editAddons.length === 0 ? (
                                      <div className="text-xs text-muted fst-italic p-8 bg-white radius-6 border text-center">
                                        No Add-ons added yet. Click "+ Add Add-on" to include custom charges.
                                      </div>
                                    ) : (
                                      <div className="d-flex flex-column gap-2">
                                        {editAddons.map((addon, aIdx) => {
                                          const itemAmount = Number(addon.amount) || 0;
                                          const itemGst = addon.hasGst ? Math.round(itemAmount * 0.18) : 0;
                                          const itemTotal = itemAmount + itemGst;

                                          return (
                                            <div key={aIdx} className="p-8 radius-6 border bg-white d-flex flex-column gap-2">
                                              <div className="d-flex align-items-center gap-2">
                                                <input
                                                  type="text"
                                                  className="form-control text-xs py-2 px-8"
                                                  placeholder="Add-on Title (e.g. Registration Fees)"
                                                  value={addon.title}
                                                  onChange={(e) => {
                                                    const val = e.target.value;
                                                    setEditAddons((prev) =>
                                                      prev.map((item, idx) => (idx === aIdx ? { ...item, title: val } : item))
                                                    );
                                                  }}
                                                />
                                                <div className="input-group input-group-sm" style={{ width: "130px" }}>
                                                  <span className="input-group-text px-6 text-xs">₹</span>
                                                  <input
                                                    type="number"
                                                    className="form-control text-xs py-2 px-6"
                                                    placeholder="Price"
                                                    value={addon.amount}
                                                    onChange={(e) => {
                                                      const val = e.target.value;
                                                      setEditAddons((prev) =>
                                                        prev.map((item, idx) => (idx === aIdx ? { ...item, amount: val } : item))
                                                      );
                                                    }}
                                                  />
                                                </div>
                                                <button
                                                  type="button"
                                                  className="btn btn-sm btn-outline-danger p-4 d-flex align-items-center justify-content-center radius-4"
                                                  style={{ width: "28px", height: "28px" }}
                                                  onClick={() => {
                                                    setEditAddons((prev) => prev.filter((_, idx) => idx !== aIdx));
                                                  }}
                                                  title="Remove Add-on"
                                                >
                                                  <Icon icon="solar:trash-bin-trash-bold" />
                                                </button>
                                              </div>

                                              <div className="d-flex align-items-center justify-content-between text-xs ps-4">
                                                <div className="form-check d-flex align-items-center gap-2 m-0 p-0">
                                                  <input
                                                    type="checkbox"
                                                    className="form-check-input mt-0"
                                                    id={`edit-addon-gst-${aIdx}`}
                                                    checked={!!addon.hasGst}
                                                    onChange={(e) => {
                                                      const checked = e.target.checked;
                                                      setEditAddons((prev) =>
                                                        prev.map((item, idx) => (idx === aIdx ? { ...item, hasGst: checked } : item))
                                                      );
                                                    }}
                                                    style={{ cursor: "pointer" }}
                                                  />
                                                  <label
                                                    htmlFor={`edit-addon-gst-${aIdx}`}
                                                    className="form-check-label text-secondary-light fw-semibold"
                                                    style={{ cursor: "pointer" }}
                                                  >
                                                    {addon.hasGst ? "GST Applied (18%)" : "Apply GST (18%)"}
                                                  </label>
                                                </div>

                                                {itemAmount > 0 && (
                                                  <div className="text-secondary-light">
                                                    <span>
                                                      GST:{" "}
                                                      <strong className={addon.hasGst ? "text-warning-700" : "text-dark"}>
                                                        ₹{formatIndianNumber(itemGst)}
                                                      </strong>
                                                    </span>
                                                    <span className="ms-3">
                                                      Total: <strong className="text-success-600">₹{formatIndianNumber(itemTotal)}</strong>
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {isCurrentlyEditing ? (
                                  <div className="col-12 mt-8 pt-8 border-top">
                                    <span className="text-secondary-light fw-bold d-block mb-4">Remarks:</span>
                                    <textarea
                                      className="form-control text-xs"
                                      rows="2"
                                      placeholder="Enter remarks..."
                                      value={editRemarks}
                                      onChange={(e) => setEditRemarks(e.target.value)}
                                    ></textarea>
                                  </div>
                                ) : (
                                  stallBlock.remarks && (
                                    <div className="col-12 mt-8 pt-8 border-top">
                                      <span className="text-secondary-light fw-bold">Remarks:</span>{" "}
                                      <span className="text-dark fw-medium">{stallBlock.remarks}</span>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  {selectedBlockItem.products && selectedBlockItem.products.length > 0 && (
                    <div className="col-md-6 mt-12">
                      <div className="text-xs text-secondary-light fw-bold mb-8">Products</div>
                      <div className="d-flex flex-wrap gap-2">
                        {selectedBlockItem.products.map((p, pi) => (
                          <span key={pi} className="badge bg-primary-100 text-primary-800 px-8 py-4 radius-4 text-xs">
                            {typeof p === "object" ? p.productName || p.name || p._id : p}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedBlockItem.interestedLocations && selectedBlockItem.interestedLocations.length > 0 && (
                    <div className="col-md-6 mt-12">
                      <div className="text-xs text-secondary-light fw-bold mb-8">Interested Locations</div>
                      <div className="d-flex flex-wrap gap-2">
                        {selectedBlockItem.interestedLocations.map((l, li) => (
                          <span key={li} className="badge bg-info-100 text-info-800 px-8 py-4 radius-4 text-xs">
                            {typeof l === "object" ? l.name || l.locationName || l._id : l}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payment Details & History */}
                  {(() => {
                    const hasPendingPayments =
                      selectedBlockPayments && selectedBlockPayments.some((p) => p.paymentStatus === "Pending");

                    const getUniqueStallAmount = () => {
                      const bDetails = (selectedBlockItem.statusDetails || []).filter(
                        (d) => d.status === "Blocked" || d.status === "Block" || d.status === "Booked"
                      );
                      const uStalls = {};
                      let hasStall = false;
                      bDetails.forEach((d) => {
                        if (d.stallNo) {
                          hasStall = true;
                          if (!uStalls[d.stallNo]) uStalls[d.stallNo] = d.amount || 0;
                          else uStalls[d.stallNo] = d.amount || uStalls[d.stallNo];
                        }
                      });
                      if (hasStall) {
                        return Object.values(uStalls).reduce((sum, val) => sum + Number(val || 0), 0);
                      }
                      const lastBooked = [...(selectedBlockItem.statusDetails || [])]
                        .reverse()
                        .find((d) => d.status === "Booked" || d.status === "Blocked" || d.status === "Block");
                      return lastBooked ? Number(lastBooked.amount || 0) : 0;
                    };
                    const totalPaymentAmount = getUniqueStallAmount();
                    const paidAmount = (selectedBlockPayments || [])
                      .filter((p) => p.paymentStatus === "Completed")
                      .reduce((sum, p) => sum + (Number(p.amountPaid) || 0), 0);
                    const balancePendingAmount = totalPaymentAmount - paidAmount;

                    return (
                      <div className="col-12 mt-16">
                        <h6 className="text-xs fw-bold text-secondary-light uppercase mb-12 border-bottom pb-8">Stall Payment History</h6>

                        <div className="d-flex align-items-center justify-content-between mb-16 bg-light p-12 radius-8 border">
                          <div className="text-center w-100 border-end">
                            <div className="text-xs text-secondary-light fw-bold mb-4">Total Amount</div>
                            <div className="text-sm fw-bold text-dark">₹{totalPaymentAmount.toLocaleString("en-IN")}</div>
                          </div>
                          <div className="text-center w-100 border-end">
                            <div className="text-xs text-secondary-light fw-bold mb-4">Paid Amount</div>
                            <div className="text-sm fw-bold text-success">₹{paidAmount.toLocaleString("en-IN")}</div>
                          </div>
                          <div className="text-center w-100">
                            <div className="text-xs text-secondary-light fw-bold mb-4">Balance Pending</div>
                            <div className="text-sm fw-bold text-danger">₹{balancePendingAmount.toLocaleString("en-IN")}</div>
                          </div>
                        </div>

                        <div className="table-responsive shadow-sm radius-8 border scroll-sm mb-16" style={{ maxHeight: "200px", overflowY: "auto" }}>
                          <table className="table table-sm mb-0">
                            <thead className="bg-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                              <tr>
                                <th className="text-xs fw-bold px-12 py-8">S.No</th>
                                <th className="text-xs fw-bold px-12 py-8">Date</th>
                                <th className="text-xs fw-bold px-12 py-8">Payment Method</th>
                                <th className="text-xs fw-bold px-12 py-8">Transaction ID / Proof</th>
                                <th className="text-xs fw-bold px-12 py-8 text-end">Amount Paid</th>
                                <th className="text-xs fw-bold px-12 py-8" style={{ minWidth: "150px" }}>Remarks</th>
                                <th className="text-xs fw-bold px-12 py-8 text-center">Status</th>
                                {hasPendingPayments && <th className="text-xs fw-bold px-12 py-8 text-center" style={{ width: "160px" }}>Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {selectedBlockPayments && selectedBlockPayments.length > 0 ? (
                                selectedBlockPayments.map((p, idx) => (
                                  <tr key={idx}>
                                    <td className="text-xs px-12 py-8">{idx + 1}</td>
                                    <td className="text-xs px-12 py-8">{formatDate(p.createdAt)}</td>
                                    <td className="text-xs px-12 py-8 fw-semibold">{p.paymentMethod}</td>
                                    <td className="text-xs px-12 py-8">
                                      {p.paymentMethod === "Cheque" ? (
                                        <div className="d-flex flex-column" style={{ fontSize: "11px", lineHeight: "1.4" }}>
                                          <span><strong>Cheque No:</strong> {p.transactionId || "—"}</span>
                                          <span><strong>Bank:</strong> {p.bankName || "—"}</span>
                                          <span><strong>Branch:</strong> {p.branchName || "—"}</span>
                                          {p.proofUrl && (
                                            <a href={getImageUrl(p.proofUrl)} target="_blank" rel="noreferrer" className="text-primary fw-bold text-decoration-underline mt-4 d-inline-block">
                                              View Cheque Copy
                                            </a>
                                          )}
                                        </div>
                                      ) : p.proofUrl ? (
                                        <a href={getImageUrl(p.proofUrl)} target="_blank" rel="noreferrer" className="text-primary fw-bold text-decoration-underline text-sm">
                                          View Proof
                                        </a>
                                      ) : (
                                        p.transactionId || "—"
                                      )}
                                    </td>
                                    <td className="text-xs px-12 py-8 text-end fw-bold text-success-600">₹{formatIndianNumber(p.amountPaid)}</td>
                                    <td className="text-xs px-12 py-8" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{p.remarks || "—"}</td>
                                    <td className="text-xs px-12 py-8 text-center">
                                      <span
                                        className={`badge px-8 py-2 radius-4 text-xs ${p.paymentStatus === "Completed"
                                          ? "bg-success-100 text-success-800"
                                          : p.paymentStatus === "Pending"
                                            ? "bg-warning-100 text-warning-800"
                                            : "bg-danger-100 text-danger-800"
                                          }`}
                                      >
                                        {p.paymentStatus}
                                      </span>
                                    </td>
                                    {hasPendingPayments && (
                                      <td className="text-xs px-12 py-8 text-center">
                                        {p.paymentStatus === "Pending" ? (
                                          <div className="d-flex align-items-center justify-content-center gap-2">
                                            <button
                                              type="button"
                                              className="btn btn-xs btn-success radius-4 py-4 px-8 text-xs fw-bold border-0 d-flex align-items-center gap-1"
                                              style={{ backgroundColor: "#10b981", color: "#fff" }}
                                              onClick={async () => {
                                                const verifyRes = await verifyPaymentStatus(p._id, "Completed");
                                                if (verifyRes.status) {
                                                  const res = await getClientFollowupPayments(selectedBlockItem._id);
                                                  if (res.status) {
                                                    setSelectedBlockPayments(res.response?.data || res.response || []);
                                                  }
                                                  fetchClientFollowupsList(
                                                    selectedExpo?._id,
                                                    clientFollowupStatus,
                                                    clientFollowupsPage,
                                                    clientFollowupsLimit,
                                                    clientSearchInput,
                                                    selectedManagerFilter
                                                  );
                                                }
                                              }}
                                            >
                                              Approve
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-xs btn-danger radius-4 py-4 px-8 text-xs fw-bold border-0 d-flex align-items-center gap-1"
                                              style={{ backgroundColor: "#ef4444", color: "#fff" }}
                                              onClick={async () => {
                                                const verifyRes = await verifyPaymentStatus(p._id, "Failed");
                                                if (verifyRes.status) {
                                                  const res = await getClientFollowupPayments(selectedBlockItem._id);
                                                  if (res.status) {
                                                    setSelectedBlockPayments(res.response?.data || res.response || []);
                                                  }
                                                  fetchClientFollowupsList(
                                                    selectedExpo?._id,
                                                    clientFollowupStatus,
                                                    clientFollowupsPage,
                                                    clientFollowupsLimit,
                                                    clientSearchInput,
                                                    selectedManagerFilter
                                                  );
                                                }
                                              }}
                                            >
                                              Reject
                                            </button>
                                          </div>
                                        ) : (
                                          <span className="text-xs text-secondary-light fw-semibold">—</span>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={hasPendingPayments ? "8" : "7"} className="text-center text-xs py-16 text-secondary-light">
                                    No payment transaction history logged yet.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Collect Payment Form Section (Admin Direct Entry) */}
                  <div className="col-12 mt-16">
                    <div className="p-16 border radius-12 bg-light shadow-sm">
                      <h6 className="text-xs fw-bold text-secondary-light uppercase mb-12 border-bottom pb-8 d-flex align-items-center gap-2">
                        <Icon icon="solar:wallet-money-bold" className="text-primary-600 text-lg" />
                        Collect Payment (Admin Direct Entry)
                      </h6>
                      <form onSubmit={handleAdminPaymentSubmit} className="row g-3 text-xs">
                        <div className="col-md-3">
                          <label className="form-label fw-bold text-secondary-light mb-4">Payment Method *</label>
                          <select
                            className="form-select form-select-sm radius-6"
                            value={adminPaymentMethod}
                            onChange={(e) => setAdminPaymentMethod(e.target.value)}
                            required
                          >
                            <option value="">Select Method</option>
                            <option value="UPI">UPI</option>
                            <option value="Cash">Cash</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="Net Banking">Net Banking</option>
                            <option value="Cheque">Cheque</option>
                          </select>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label fw-bold text-secondary-light mb-4">Amount *</label>
                          <input
                            type="number"
                            className="form-control form-control-sm radius-6"
                            placeholder="Enter amount"
                            value={adminPaymentAmount}
                            onChange={(e) => setAdminPaymentAmount(e.target.value)}
                            required
                            min="1"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label fw-bold text-secondary-light mb-4">
                            Payment Proof {adminPaymentMethod === "UPI" && "*"}
                          </label>
                          <input
                            id="adminPaymentProofInput"
                            type="file"
                            className="form-control form-control-sm radius-6"
                            accept="image/*"
                            onChange={(e) => setAdminPaymentProofFile(e.target.files[0])}
                            required={adminPaymentMethod === "UPI"}
                            disabled={adminPaymentMethod === ""}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label fw-bold text-secondary-light mb-4">
                            Remarks {adminPaymentMethod === "Cash" && "*"}
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm radius-6"
                            placeholder="Remarks"
                            value={adminPaymentRemarks}
                            onChange={(e) => setAdminPaymentRemarks(e.target.value)}
                            required={adminPaymentMethod === "Cash"}
                          />
                        </div>
                        {adminPaymentMethod === "Cheque" && (
                          <>
                            <div className="col-md-3">
                              <label className="form-label fw-bold text-secondary-light mb-4">Cheque No / Transaction ID *</label>
                              <input
                                type="text"
                                className="form-control form-control-sm radius-6"
                                placeholder="Enter Cheque No"
                                value={adminPaymentTransactionId}
                                onChange={(e) => setAdminPaymentTransactionId(e.target.value)}
                                required
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label fw-bold text-secondary-light mb-4">Bank Name *</label>
                              <input
                                type="text"
                                className="form-control form-control-sm radius-6"
                                placeholder="Enter Bank Name"
                                value={adminPaymentBankName}
                                onChange={(e) => setAdminPaymentBankName(e.target.value)}
                                required
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label fw-bold text-secondary-light mb-4">Branch Name *</label>
                              <input
                                type="text"
                                className="form-control form-control-sm radius-6"
                                placeholder="Enter Branch Name"
                                value={adminPaymentBranchName}
                                onChange={(e) => setAdminPaymentBranchName(e.target.value)}
                                required
                              />
                            </div>
                          </>
                        )}
                        <div className="col-12 text-end">
                          <button
                            type="submit"
                            className="btn btn-sm text-white fw-bold px-16 radius-6"
                            style={{ backgroundColor: "#16a34a" }}
                            disabled={adminPaymentSubmitting}
                          >
                            {adminPaymentSubmitting ? "Submitting..." : "Submit Payment"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top p-16 bg-light radius-bottom-16 d-flex justify-content-between align-items-center">
                <button
                  type="button"
                  className="btn btn-secondary radius-8 px-24"
                  onClick={() => {
                    setShowBlockDetailModal(false);
                    setSelectedBlockItem(null);
                    setSelectedBlockPayments([]);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PastExpoLayer;
